import clsx from 'clsx';

interface PlayerRowProps {
	playerNames: string[];
	index: number;
	side: 'left' | 'right';
}
interface NextInPlayer {
	playerOneName: string;
	playerTwoName?: string;
	rotationUuid: string;
}

interface NextInPlayersListProps {
	nextInTeamOne: NextInPlayer[];
	nextInTeamTwo: NextInPlayer[];
	isMatchOverAndCompleted: boolean;
	teamOneTitle: string;
	teamTwoTitle: string;
}

const PlayerRow = ({ playerNames, side }: PlayerRowProps) => {
	const isRight = side === 'right';

	return (
		<div
			className={clsx(
				'flex flex-col leading-tight text-sm w-full max-w-[200px]',
				isRight ? 'ml-auto items-start text-left' : 'items-start text-left'
			)}
		>
			{playerNames.map((name, i) => (
				<span key={i}>{name}</span>
			))}
		</div>
	);
};

export const ResponsiveNextInPlayersList = (props: NextInPlayersListProps) => {
	return (
		<>
			{/* Mobile */}
			<div className="sm:hidden">
				<NextInPlayersListMobile {...props} />
			</div>

			{/* Desktop */}
			<div className="hidden sm:block">
				<NextInPlayersList {...props} />
			</div>
		</>
	);
};

export const NextInPlayersList = ({ nextInTeamOne, nextInTeamTwo, isMatchOverAndCompleted }: NextInPlayersListProps) => {
	if (isMatchOverAndCompleted) return null;

	const maxLength = Math.max(nextInTeamOne.length, nextInTeamTwo.length);

	return (
		<div className="pt-4">
			<div className="text-center p-6 border rounded-xl border-gray-200">
				<p className="pb-4 font-semibold text-sm text-left">Next in:</p>

				<div className="flex flex-col gap-3">
					{Array.from({ length: maxLength }).map((_, index) => {
						const playerOne = nextInTeamOne[index];
						const playerTwo = nextInTeamTwo[index];

						return (
							<div key={index} className="grid grid-cols-[1fr_auto_1fr] items-center justify-between gap-2 w-full">
								{playerTwo ? (
									<PlayerRow
										playerNames={
											playerTwo ? ([playerTwo.playerOneName, playerTwo.playerTwoName].filter(Boolean) as string[]) : []
										}
										index={index + 1}
										side="left"
									/>
								) : (
									<div />
								)}

								<span className="text-gray-500 text-sm text-center">v.</span>

								{playerOne ? (
									<PlayerRow
										playerNames={
											playerOne ? ([playerOne.playerOneName, playerOne.playerTwoName].filter(Boolean) as string[]) : []
										}
										index={index + 1}
										side="right"
									/>
								) : (
									<div />
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export const NextInPlayersListMobile = ({
	nextInTeamOne,
	nextInTeamTwo,
	isMatchOverAndCompleted,
	teamOneTitle,
	teamTwoTitle
}: NextInPlayersListProps) => {
	if (isMatchOverAndCompleted) return null;

	const renderPlayers = (players: NextInPlayer[]) =>
		players.map((player) => (
			<div key={player.rotationUuid} className="flex items-start gap-2">
				<div className="flex flex-col text-sm text-gray-800">
					<span>{player.playerOneName}</span>
					{player.playerTwoName && <span>{player.playerTwoName}</span>}
				</div>
			</div>
		));

	return (
		<div className="pt-4 sm:hidden">
			<div className="p-6 border rounded-xl border-gray-200">
				<p className="pb-4 font-semibold text-sm text-left">Next in:</p>

				<div className="flex flex-col gap-4">
					{/* Team One */}
					<div className="flex flex-col gap-1">
						<p className="text-blue-600 font-semibold text-sm">{teamOneTitle} (A)</p>
						{renderPlayers(nextInTeamOne)}
					</div>

					{/* Team Two */}
					<div className="flex flex-col gap-1">
						<p className="text-blue-600 font-semibold text-sm">{teamTwoTitle} (H)</p>
						{renderPlayers(nextInTeamTwo)}
					</div>
				</div>
			</div>
		</div>
	);
};
